import {
  CENTRAL_AUTH_PAGE_PATHS,
  COMPANY_SIGN_IN_TEST_PASSED,
  FEDERATED_SIGNUP_PATH,
  ssoProviderSlugSchema,
  federatedProviderSlugSchema,
  federatedStateSchema,
  type FederatedProviderSlug,
  type CompleteFederatedSignupDTO,
} from '@instanexus/contracts';
import { errorMessage, useTranslation } from '@instanexus/i18n';
import { isApiError } from '@instanexus/sdk';
import { Button, Card, CardDescription, CardFooter, CardHeader } from '@instanexus/ui';
import { setAccessToken } from '@instanexus/web-auth';
import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';

import { AuthCardLayout } from '@/components/auth-card-layout';
import { FederatedConfirmForm } from '@/components/federated-confirm-form';
import { useApiClient } from '@/hooks/use-api-client';
import { centralLoginPath } from '@/lib/auth-paths';
import { finishCentralSignIn } from '@/lib/central-session';
import { companySignInSettingsUrl } from '@/lib/company-sign-in-test';
import { takeFederatedReturn, type FederatedReturn } from '@/lib/federated-return';
import { FEDERATED_RESTART_CODE, restartFederatedRoundTrip } from '@/lib/federated-restart';
import {
  SIGN_UP_LANDING_REQUEST,
  parseSignInRequest,
  type SignInRequest,
} from '@/lib/sign-in-request';

/** Router location state the sign-in page reads. */
export interface FederatedRefusalState {
  readonly federatedErrorCode: string;
}

const UNEXPECTED_FAILURE = 'UNEXPECTED';
const CONFIRMATION_REQUIRED_CODE = 'FEDERATED_EMAIL_CONFIRMATION_REQUIRED';

/** Where a refusal sends the person: the page they started from. */
function loginTargetFor(ret: FederatedReturn | null): string {
  return ret?.kind === 'signup'
    ? CENTRAL_AUTH_PAGE_PATHS.signup
    : ret?.kind === 'sign_in'
      ? `/login?${ret.search}`
      : ret?.kind === 'invite'
        ? centralLoginPath(ret.client)
        : '/login';
}

/** Full-page navigation; the only way out of the central app. */
function redirectTo(url: string): void {
  window.location.assign(url);
}

/**
 * Where a provider lands after the person signs in there (`/auth/:provider/callback`). Completes the
 * sign-in with the API, then finishes like a password sign-in; every refusal returns to the sign-in
 * page the person came from, with the code in router state so the page shows the translated message.
 */
export function FederatedCallbackPage(): ReactElement {
  const { t } = useTranslation('auth');
  const api = useApiClient();
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const [failure, setFailure] = useState<{ code: string; loginTarget: string } | null>(null);
  const [confirming, setConfirming] = useState<{
    provider: FederatedProviderSlug;
    ret: FederatedReturn | null;
  } | null>(null);

  /** Finishes a completed provider sign-in or a confirmed link: the same handoff either way. */
  const finishWith = useCallback(
    async (ret: FederatedReturn | null, accessToken: string): Promise<void> => {
      const request: SignInRequest =
        ret?.kind === 'sign_in'
          ? parseSignInRequest(new URLSearchParams(ret.search))
          : {
              client: ret?.kind === 'invite' ? ret.client : 'admin',
              returnTo: '/',
              handoff: null,
              email: null,
            };
      const search = ret?.kind === 'sign_in' ? ret.search : `client=${request.client}`;
      try {
        await finishCentralSignIn(api, request, search, accessToken, {
          navigate: (to) => void navigate(to),
          redirectTo,
        });
      } catch (error: unknown) {
        setFailure({
          code: isApiError(error) ? error.code : UNEXPECTED_FAILURE,
          loginTarget: loginTargetFor(ret),
        });
      }
    },
    [api, navigate],
  );

  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const ret = takeFederatedReturn();
    const loginTarget = loginTargetFor(ret);

    const refuse = (code: string): void => {
      void navigate(loginTarget, {
        replace: true,
        state: { federatedErrorCode: code } satisfies FederatedRefusalState,
      });
    };

    // FEDERATED_SIGN_IN_RESTART: one more round trip (ADR 0039 decision 10); anything else is a refusal.
    const refuseOrRestart = async (
      error: unknown,
      provider: FederatedProviderSlug,
    ): Promise<void> => {
      const code = isApiError(error) ? error.code : UNEXPECTED_FAILURE;
      if (code === CONFIRMATION_REQUIRED_CODE && ret?.kind !== 'signup') {
        setConfirming({ provider, ret });
        return;
      }
      if (code === FEDERATED_RESTART_CODE) {
        try {
          if (await restartFederatedRoundTrip(api, provider, ret, redirectTo)) return;
        } catch (restartError: unknown) {
          return refuse(isApiError(restartError) ? restartError.code : UNEXPECTED_FAILURE);
        }
      }
      return refuse(code);
    };

    // A company test never signs anyone in: every outcome leaves for the app's Settings page.
    const runCompanyTest = async (
      client: Extract<FederatedReturn, { kind: 'company_test' }>['client'],
    ): Promise<void> => {
      const leave = (result: string): void => {
        const url = companySignInSettingsUrl(client, result);
        if (url === null) setFailure({ code: UNEXPECTED_FAILURE, loginTarget: '/login' });
        else redirectTo(url);
      };
      const provider = ssoProviderSlugSchema.safeParse(params.provider);
      if (!provider.success) return leave('SSO_PROVIDER_UNAVAILABLE');
      if (searchParams.has('error')) return leave('FEDERATED_PROVIDER_ERROR');
      const code = searchParams.get('code');
      const state = federatedStateSchema.safeParse(searchParams.get('state'));
      if (code === null || code === '' || !state.success) return leave('FEDERATED_STATE_INVALID');
      try {
        const session = await api.auth.refreshSession();
        if (session === undefined) return leave('SIGN_IN_REQUIRED');
        setAccessToken(session.accessToken);
        await api.federation.completeCompanyTest(provider.data, { state: state.data, code });
        return leave(COMPANY_SIGN_IN_TEST_PASSED);
      } catch (error: unknown) {
        return leave(isApiError(error) ? error.code : UNEXPECTED_FAILURE);
      }
    };

    const run = async (): Promise<void> => {
      if (ret?.kind === 'company_test') return runCompanyTest(ret.client);
      const provider = federatedProviderSlugSchema.safeParse(params.provider);
      if (!provider.success) return refuse('FEDERATED_PROVIDER_DISABLED');
      if (searchParams.has('error')) return refuse('FEDERATED_PROVIDER_ERROR');
      const code = searchParams.get('code');
      const state = federatedStateSchema.safeParse(searchParams.get('state'));
      if (code === null || code === '' || !state.success) return refuse('FEDERATED_STATE_INVALID');

      if (ret?.kind === 'signup') {
        let result: CompleteFederatedSignupDTO;
        try {
          result = await api.federation.completeSignup(provider.data, {
            state: state.data,
            code,
          });
        } catch (error: unknown) {
          return refuseOrRestart(error, provider.data);
        }
        if (result.outcome === 'pending') {
          void navigate(FEDERATED_SIGNUP_PATH, { replace: true });
          return;
        }
        try {
          await finishCentralSignIn(
            api,
            SIGN_UP_LANDING_REQUEST,
            'client=admin',
            result.session.accessToken,
            { navigate: (to) => void navigate(to), redirectTo },
          );
        } catch (error: unknown) {
          setFailure({ code: isApiError(error) ? error.code : UNEXPECTED_FAILURE, loginTarget });
        }
        return;
      }

      let accessToken: string;
      try {
        const session = await api.federation.complete(provider.data, { state: state.data, code });
        accessToken = session.accessToken;
      } catch (error: unknown) {
        return refuseOrRestart(error, provider.data);
      }

      await finishWith(ret, accessToken);
    };
    void run();
  }, [api, finishWith, navigate, params.provider, searchParams]);

  if (failure !== null) {
    return (
      <AuthCardLayout>
        <Card className="w-full max-w-md">
          <CardHeader>
            <h1 className="text-3xl font-semibold text-primary text-balance">
              {t('central.callbackFailedTitle')}
            </h1>
            <CardDescription role="alert">{errorMessage(t, failure.code)}</CardDescription>
          </CardHeader>
          <CardFooter>
            <Button asChild>
              <Link to={failure.loginTarget}>{t('central.signInAgain')}</Link>
            </Button>
          </CardFooter>
        </Card>
      </AuthCardLayout>
    );
  }

  if (confirming !== null) {
    return (
      <AuthCardLayout>
        <Card className="w-full max-w-md">
          <FederatedConfirmForm
            provider={confirming.provider}
            startOverTo={loginTargetFor(confirming.ret)}
            onConfirmed={(accessToken) => {
              void finishWith(confirming.ret, accessToken);
            }}
            onRestart={() =>
              restartFederatedRoundTrip(api, confirming.provider, confirming.ret, redirectTo)
            }
          />
        </Card>
      </AuthCardLayout>
    );
  }

  return (
    <AuthCardLayout>
      <Card className="w-full max-w-md">
        <CardHeader>
          <p role="status" className="text-muted-foreground">
            {t('central.finishing')}
          </p>
        </CardHeader>
      </Card>
    </AuthCardLayout>
  );
}
